import React, { useEffect, useState, useCallback, useMemo } from "react";
import { AppShell } from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { api, extractErr } from "@/lib/api";
import { Skeleton, EmptyState } from "@/components/EmptyState";
import { PlanGate, featureAllowedByPlan } from "@/components/PlanGate";
import { useAuth } from "@/lib/auth";
import { Combobox } from "@/components/Combobox";
import {
  Layers3, Save, Package2, Box, Factory, Info, Plus, Trash2, FileText, Edit3, ListChecks, Search, Copy,
} from "lucide-react";

const SIZES = ["100ml", "300ml", "1L", "5L", "10L", "20L"];
const DEFAULT_PACK = { "100ml": 12, "300ml": 12, "1L": 6, "5L": 1, "10L": 1, "20L": 1 };

export default function BOM() {
  const { company } = useAuth();
  const allowed = featureAllowedByPlan(company?.plan || "free_trial", "bom");
  const [items, setItems] = useState([]);
  // Phase 9: each size can have multiple named variants. Map size -> array of variants.
  const [bomsBySize, setBomsBySize] = useState({});
  const [productBoms, setProductBoms] = useState([]); // list of per-product BOMs
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    if (!allowed) { setLoading(false); return; }
    setLoading(true);
    try {
      const [inv, sb, pb] = await Promise.all([
        api.get("/inventory?limit=2000"),
        api.get("/size-bom"),
        api.get("/production/bom"),
      ]);
      setItems(inv.data.items || []);
      const map = {};
      (sb.data.size_boms || []).forEach((b) => {
        if (!map[b.size]) map[b.size] = [];
        map[b.size].push(b);
      });
      Object.keys(map).forEach((sz) => {
        map[sz].sort((a, b) => {
          if (a.is_default !== b.is_default) return a.is_default ? -1 : 1;
          return (a.name || "").localeCompare(b.name || "");
        });
      });
      setBomsBySize(map);
      setProductBoms(pb.data.boms || []);
    } catch (e) { toast.error(extractErr(e)); }
    setLoading(false);
  }, [allowed]);

  useEffect(() => { load(); }, [load]);

  if (!allowed) {
    return (
      <AppShell title="Bill of Materials" subtitle="Recipes and packaging logic">
        <PlanGate feature="BOM (recipes & packaging)" planRequired="Growth"><div className="h-32" /></PlanGate>
      </AppShell>
    );
  }

  return (
    <AppShell title="Bill of Materials" subtitle="Configure recipes and packaging consumption per product or size">
      <Tabs defaultValue="product" className="w-full">
        <TabsList className="mb-4" data-testid="bom-tabs-list">
          <TabsTrigger value="product" data-testid="bom-tab-product"><ListChecks className="w-4 h-4 mr-1.5" /> Product BOMs</TabsTrigger>
          <TabsTrigger value="size" data-testid="bom-tab-size"><Layers3 className="w-4 h-4 mr-1.5" /> Size BOM (Packaging)</TabsTrigger>
        </TabsList>

        <TabsContent value="product" className="m-0">
          <ProductBOMPanel
            items={items}
            boms={productBoms}
            loading={loading}
            onChanged={load}
          />
        </TabsContent>

        <TabsContent value="size" className="m-0">
          <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-4 mb-4 flex gap-3">
            <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
            <div className="text-sm text-muted-foreground">
              Each size can hold multiple named <b>variants</b> (e.g. <i>Standard, Premium, Eco</i>) — useful when different products in the same size use different bottles, caps, or boxes. <b>One variant per size is the default</b> and is used by production orders that don't pick a specific variant. Use <b>Duplicate</b> to clone a recipe and tweak it.
            </div>
          </div>

          {loading ? (
            <div className="space-y-3">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-32" />)}</div>
          ) : (
            <div className="space-y-4">
              {SIZES.map((sz) => (
                <SizeBomGroup
                  key={sz}
                  size={sz}
                  variants={bomsBySize[sz] || []}
                  items={items}
                  defaultPack={DEFAULT_PACK[sz]}
                  onChanged={load}
                />
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </AppShell>
  );
}

// ====================================================================
// PRODUCT BOMs (Phase 9) — recipes per finished product, reusable across sizes
// ====================================================================

function ProductBOMPanel({ items, boms, loading, onChanged }) {
  const [search, setSearch] = useState("");
  const [editing, setEditing] = useState(null);  // BOM doc being edited or "new"
  const [deleting, setDeleting] = useState(null); // BOM doc being deleted

  // Index inventory items by id for quick lookups
  const itemById = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.id] = i; });
    return m;
  }, [items]);

  // Filter to view
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return boms;
    return boms.filter((b) =>
      (b.product_name || "").toLowerCase().includes(q)
      || (b.product_sku || "").toLowerCase().includes(q)
      || (b.notes || "").toLowerCase().includes(q)
    );
  }, [boms, search]);

  const handleDelete = async (bom) => {
    try {
      await api.delete(`/production/bom/${bom.id}`);
      toast.success(`Deleted BOM for ${bom.product_name}`);
      setDeleting(null);
      await onChanged();
    } catch (e) { toast.error(extractErr(e)); }
  };

  return (
    <>
      <div className="rounded-2xl border border-primary/30 bg-primary/[0.04] p-4 mb-4 flex gap-3">
        <Info className="w-5 h-5 text-primary shrink-0 mt-0.5" />
        <div className="text-sm text-muted-foreground">
          Define a recipe per finished good — raw materials and packaging it consumes per unit. When a production order is marked <b>Completed</b>, OPERON auto-deducts each component (scaled by quantity and size factor). One BOM per product is reusable across all sizes.
        </div>
      </div>

      <div className="flex flex-col md:flex-row md:items-center gap-3 mb-4">
        <div className="relative flex-1">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input
            placeholder="Search by product, SKU or notes…"
            className="pl-9"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            data-testid="product-bom-search"
          />
        </div>
        <Button onClick={() => setEditing("new")} data-testid="product-bom-create-button">
          <Plus className="w-4 h-4 mr-1.5" /> New Product BOM
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-28" />)}</div>
      ) : filtered.length === 0 ? (
        <EmptyState
          icon={ListChecks}
          title={search ? "No matches" : "No Product BOMs yet"}
          description={search ? "Try a different search term." : "Create your first recipe to auto-deduct raw materials and packaging when a production order completes."}
          action={!search && (
            <Button onClick={() => setEditing("new")} data-testid="product-bom-empty-create">
              <Plus className="w-4 h-4 mr-1.5" /> New Product BOM
            </Button>
          )}
        />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
          {filtered.map((b) => (
            <ProductBOMCard
              key={b.id}
              bom={b}
              itemById={itemById}
              onEdit={() => setEditing(b)}
              onDelete={() => setDeleting(b)}
            />
          ))}
        </div>
      )}

      <ProductBOMForm
        open={!!editing}
        initial={editing === "new" ? null : editing}
        items={items}
        existingBoms={boms}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await onChanged();
        }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this Product BOM?</AlertDialogTitle>
            <AlertDialogDescription>
              The recipe for <b>{deleting?.product_name}</b> will be removed.
              Already-deducted production orders are not affected.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && handleDelete(deleting)} data-testid="product-bom-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}

function ProductBOMCard({ bom, itemById, onEdit, onDelete }) {
  const componentCount = (bom.components || []).length;

  // Categorize components for at-a-glance summary
  const breakdown = useMemo(() => {
    const groups = { raw: 0, pack: 0, other: 0 };
    (bom.components || []).forEach((c) => {
      const item = itemById[c.item_id] || {};
      const cat = (item.category || "").toLowerCase();
      if (cat.includes("packag")) groups.pack += 1;
      else if (cat.includes("raw")) groups.raw += 1;
      else groups.other += 1;
    });
    return groups;
  }, [bom.components, itemById]);

  return (
    <div className="rounded-2xl border border-border bg-card p-4 hover:border-primary/40 transition-colors flex flex-col" data-testid={`product-bom-card-${bom.id}`}>
      <div className="flex items-start gap-3 mb-3">
        <div className="w-10 h-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
          <FileText className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-heading font-semibold truncate" data-testid={`product-bom-name-${bom.id}`}>{bom.product_name || "—"}</div>
          <div className="text-[11px] font-mono text-muted-foreground mt-0.5">{bom.product_sku || ""}</div>
        </div>
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted">
          {componentCount} {componentCount === 1 ? "component" : "components"}
        </span>
        {breakdown.raw > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400">{breakdown.raw} raw</span>}
        {breakdown.pack > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-sky-500/10 text-sky-600 dark:text-sky-400">{breakdown.pack} packaging</span>}
        {breakdown.other > 0 && <span className="text-[11px] px-2 py-0.5 rounded-full bg-muted">{breakdown.other} other</span>}
      </div>

      <div className="space-y-1 mb-3 flex-1 min-h-0">
        {(bom.components || []).slice(0, 4).map((c) => {
          const item = itemById[c.item_id] || {};
          return (
            <div key={c.item_id} className="flex items-center justify-between text-[12px] py-0.5">
              <span className="truncate min-w-0 flex-1 text-muted-foreground">{c.item_name || item.name || c.item_id}</span>
              <span className="font-mono shrink-0 ml-2">{c.qty_per_unit} {item.unit || ""}</span>
            </div>
          );
        })}
        {componentCount > 4 && (
          <div className="text-[11px] text-muted-foreground">+ {componentCount - 4} more…</div>
        )}
      </div>

      {bom.notes && (
        <div className="text-[11px] italic text-muted-foreground mb-2 line-clamp-2">{bom.notes}</div>
      )}

      <div className="flex items-center justify-end gap-1 pt-2 border-t border-border">
        <Button variant="ghost" size="sm" onClick={onEdit} data-testid={`product-bom-edit-${bom.id}`}>
          <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
        </Button>
        <Button variant="ghost" size="sm" onClick={onDelete} className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10" data-testid={`product-bom-delete-${bom.id}`}>
          <Trash2 className="w-3.5 h-3.5 mr-1" /> Delete
        </Button>
      </div>
    </div>
  );
}

function ProductBOMForm({ open, initial, items, existingBoms, onClose, onSaved }) {
  const isEdit = !!initial;
  const [productId, setProductId] = useState("");
  const [components, setComponents] = useState([]);
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setProductId(initial.product_id || "");
      setComponents((initial.components || []).map((c) => ({
        item_id: c.item_id,
        qty_per_unit: c.qty_per_unit,
      })));
      setNotes(initial.notes || "");
    } else {
      setProductId("");
      setComponents([]);
      setNotes("");
    }
  }, [open, initial]);

  // Products = finished goods preferred, but allow any inventory item.
  // Hide products that already have a BOM (when creating new) — UNLESS we're editing.
  const productOptions = useMemo(() => {
    const usedIds = new Set(
      (existingBoms || [])
        .filter((b) => !isEdit || b.id !== initial?.id)
        .map((b) => b.product_id)
    );
    const list = items.filter((i) => !usedIds.has(i.id));
    list.sort((a, b) => {
      const aIsFG = (a.category || "").toLowerCase().includes("finished") ? 0 : 1;
      const bIsFG = (b.category || "").toLowerCase().includes("finished") ? 0 : 1;
      if (aIsFG !== bIsFG) return aIsFG - bIsFG;
      return (a.name || "").localeCompare(b.name || "");
    });
    return list.map((i) => ({
      value: i.id,
      label: i.name,
      sublabel: `${i.sku} · ${i.category || "—"}`,
    }));
  }, [items, existingBoms, isEdit, initial]);

  const itemOptions = useMemo(
    () => items.map((i) => ({
      value: i.id,
      label: i.name,
      sublabel: `${i.sku} · ${i.category || "—"} · ${i.quantity ?? 0} ${i.unit || ""}`,
    })),
    [items]
  );

  const itemById = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.id] = i; });
    return m;
  }, [items]);

  const addComponent = () => {
    setComponents((p) => [...p, { item_id: "", qty_per_unit: 1 }]);
  };

  const updateComponent = (idx, patch) => {
    setComponents((p) => p.map((c, i) => i === idx ? { ...c, ...patch } : c));
  };

  const removeComponent = (idx) => {
    setComponents((p) => p.filter((_, i) => i !== idx));
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!productId) { toast.error("Please pick a finished product"); return; }
    if (components.length === 0) { toast.error("Add at least one component"); return; }
    const cleaned = components
      .map((c) => ({ ...c, qty_per_unit: Number(c.qty_per_unit) }))
      .filter((c) => c.item_id && c.qty_per_unit > 0);
    if (cleaned.length === 0) {
      toast.error("Each component needs an item and a qty per unit > 0");
      return;
    }
    // Detect duplicate items
    const seen = new Set();
    for (const c of cleaned) {
      if (seen.has(c.item_id)) {
        const nm = itemById[c.item_id]?.name || c.item_id;
        toast.error(`Duplicate component: ${nm}. Combine into a single line.`);
        return;
      }
      seen.add(c.item_id);
    }
    // Detect self-reference (product as its own component)
    if (cleaned.some((c) => c.item_id === productId)) {
      toast.error("A product cannot be a component of itself.");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/production/bom/${initial.id}`, { components: cleaned, notes });
        toast.success(`Updated BOM for ${initial.product_name}`);
      } else {
        await api.post(`/production/bom`, {
          product_id: productId,
          components: cleaned,
          notes,
        });
        toast.success("Product BOM created");
      }
      onSaved?.();
    } catch (err) {
      toast.error(extractErr(err, "Failed to save BOM"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="product-bom-form">
        <DialogHeader>
          <DialogTitle>{isEdit ? `Edit BOM — ${initial?.product_name}` : "New Product BOM"}</DialogTitle>
          <DialogDescription>
            Define what raw materials and packaging are consumed per finished unit. The recipe is reusable across all sizes; quantities are scaled by the production order's size factor at completion time.
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Finished product</Label>
            {isEdit ? (
              <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-sm">
                <span className="font-medium">{initial?.product_name}</span>
                <span className="text-muted-foreground ml-2 font-mono text-xs">{initial?.product_sku}</span>
              </div>
            ) : (
              <Combobox
                options={productOptions}
                value={productId}
                onChange={setProductId}
                placeholder="Search and pick a finished product…"
                data-testid="product-bom-product-picker"
              />
            )}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label className="text-xs uppercase tracking-wider text-muted-foreground">Components</Label>
              <Button type="button" size="sm" variant="outline" onClick={addComponent} data-testid="product-bom-add-component">
                <Plus className="w-3.5 h-3.5 mr-1" /> Add component
              </Button>
            </div>
            {components.length === 0 ? (
              <div className="rounded-lg border border-dashed border-border px-3 py-6 text-center text-sm text-muted-foreground" data-testid="product-bom-empty-components">
                Add raw materials and / or packaging items consumed per finished unit.
              </div>
            ) : (
              <div className="rounded-lg border border-border overflow-hidden">
                <div className="hidden md:grid grid-cols-12 px-3 py-2 text-[11px] uppercase tracking-wider text-muted-foreground border-b border-border bg-muted/40">
                  <div className="col-span-7">Item</div>
                  <div className="col-span-3">Qty per unit</div>
                  <div className="col-span-2 text-right">Action</div>
                </div>
                <div className="divide-y divide-border">
                  {components.map((c, idx) => (
                    <ComponentRow
                      key={idx}
                      idx={idx}
                      value={c}
                      itemOptions={itemOptions}
                      itemById={itemById}
                      onChange={(patch) => updateComponent(idx, patch)}
                      onRemove={() => removeComponent(idx)}
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Notes (optional)</Label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Process tips, version, formula reference…"
              data-testid="product-bom-notes"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="product-bom-submit">
              <Save className="w-4 h-4 mr-1.5" />
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create BOM"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ComponentRow({ idx, value, itemOptions, itemById, onChange, onRemove }) {
  const item = value.item_id ? itemById[value.item_id] : null;
  return (
    <div className="grid grid-cols-12 gap-2 px-3 py-2 items-center" data-testid={`product-bom-component-${idx}`}>
      <div className="col-span-12 md:col-span-7">
        <Combobox
          options={itemOptions}
          value={value.item_id}
          onChange={(v) => onChange({ item_id: v })}
          placeholder="Pick a raw material or packaging item…"
          data-testid={`product-bom-component-item-${idx}`}
        />
      </div>
      <div className="col-span-9 md:col-span-3">
        <div className="flex items-center gap-1.5">
          <Input
            type="number"
            min="0"
            step="any"
            value={value.qty_per_unit}
            onChange={(e) => onChange({ qty_per_unit: e.target.value })}
            data-testid={`product-bom-component-qty-${idx}`}
            className="h-9"
          />
          <span className="text-[11px] text-muted-foreground whitespace-nowrap">{item?.unit || "—"}</span>
        </div>
      </div>
      <div className="col-span-3 md:col-span-2 flex justify-end">
        <Button
          type="button"
          variant="ghost"
          size="sm"
          onClick={onRemove}
          aria-label="Remove component"
          className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10"
          data-testid={`product-bom-component-remove-${idx}`}
        >
          <Trash2 className="w-3.5 h-3.5" />
        </Button>
      </div>
    </div>
  );
}

// ====================================================================
// SIZE BOM (Phase 9 — multi-variant per size)
// ====================================================================

function SizeBomGroup({ size, variants, items, defaultPack, onChanged }) {
  const [editing, setEditing] = useState(null);     // variant doc, "new", or null
  const [deleting, setDeleting] = useState(null);   // variant doc to delete
  const [busy, setBusy] = useState("");             // id of variant being mutated

  const setDefault = async (variant) => {
    if (variant.is_default) return;
    setBusy(variant.id);
    try {
      await api.post(`/size-bom/by-id/${variant.id}/set-default`);
      toast.success(`"${variant.name}" is now the default ${size} variant`);
      await onChanged();
    } catch (e) { toast.error(extractErr(e)); }
    setBusy("");
  };

  const duplicate = async (variant) => {
    setBusy(variant.id);
    try {
      const { data } = await api.post(`/size-bom/by-id/${variant.id}/duplicate`);
      toast.success(`Duplicated as "${data.name}"`);
      await onChanged();
      // Open the new copy for editing immediately
      setTimeout(() => setEditing({ ...data }), 200);
    } catch (e) { toast.error(extractErr(e)); }
    setBusy("");
  };

  const handleDelete = async (variant) => {
    setBusy(variant.id);
    try {
      await api.delete(`/size-bom/by-id/${variant.id}`);
      toast.success(`Deleted "${variant.name}"`);
      setDeleting(null);
      await onChanged();
    } catch (e) { toast.error(extractErr(e)); }
    setBusy("");
  };

  return (
    <div className="rounded-2xl border border-border bg-card overflow-hidden" data-testid={`size-bom-group-${size}`}>
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center">
            <Layers3 className="w-4 h-4" />
          </div>
          <div>
            <div className="font-heading font-semibold">{size}</div>
            <div className="text-[11px] text-muted-foreground">
              {variants.length === 0 ? "No variants yet" : `${variants.length} variant${variants.length === 1 ? "" : "s"}`}
            </div>
          </div>
        </div>
        <Button
          size="sm"
          variant="outline"
          onClick={() => setEditing({ size, _isNew: true })}
          data-testid={`size-bom-${size}-add-variant`}
        >
          <Plus className="w-3.5 h-3.5 mr-1" /> New variant
        </Button>
      </div>

      {variants.length === 0 ? (
        <div className="px-4 py-8 text-sm text-muted-foreground text-center" data-testid={`size-bom-${size}-empty`}>
          No packaging recipe configured for {size} yet. Click <b>New variant</b> to add one.
        </div>
      ) : (
        <div className="divide-y divide-border">
          {variants.map((v) => (
            <VariantCard
              key={v.id}
              variant={v}
              items={items}
              busy={busy === v.id}
              onEdit={() => setEditing(v)}
              onDuplicate={() => duplicate(v)}
              onDelete={() => setDeleting(v)}
              onSetDefault={() => setDefault(v)}
            />
          ))}
        </div>
      )}

      <VariantFormDialog
        open={!!editing}
        initial={editing && !editing._isNew ? editing : null}
        size={editing?.size || size}
        items={items}
        defaultPack={defaultPack}
        existingVariants={variants}
        onClose={() => setEditing(null)}
        onSaved={async () => {
          setEditing(null);
          await onChanged();
        }}
      />

      <AlertDialog open={!!deleting} onOpenChange={(v) => !v && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete variant "{deleting?.name}"?</AlertDialogTitle>
            <AlertDialogDescription>
              This recipe will no longer be available for new {size} production orders.
              {deleting?.is_default && variants.length > 1 ? (
                <span className="block mt-2 text-amber-600 dark:text-amber-400">
                  This is the default — another variant will be promoted automatically.
                </span>
              ) : null}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => deleting && handleDelete(deleting)} data-testid={`size-bom-${size}-delete-confirm`}>
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function VariantCard({ variant, items, busy, onEdit, onDuplicate, onDelete, onSetDefault }) {
  const itemById = useMemo(() => {
    const m = {};
    items.forEach((i) => { m[i.id] = i; });
    return m;
  }, [items]);
  const bottle = variant.bottle_item_id ? itemById[variant.bottle_item_id] : null;
  const cap = variant.cap_item_id ? itemById[variant.cap_item_id] : null;
  const box = variant.box_item_id ? itemById[variant.box_item_id] : null;
  const filledCount = [bottle, cap, box].filter(Boolean).length;

  return (
    <div className="px-4 py-3 hover:bg-accent/30 transition-colors" data-testid={`size-bom-variant-${variant.id}`}>
      <div className="flex items-start gap-3">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className="font-medium" data-testid={`size-bom-variant-name-${variant.id}`}>{variant.name || "(unnamed)"}</span>
            {variant.is_default ? (
              <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/30" data-testid={`size-bom-default-badge-${variant.id}`}>
                Default
              </span>
            ) : (
              <button
                type="button"
                onClick={onSetDefault}
                disabled={busy}
                className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full bg-muted text-muted-foreground hover:bg-primary/10 hover:text-primary transition-colors"
                data-testid={`size-bom-set-default-${variant.id}`}
              >
                Set as default
              </button>
            )}
            <span className={`text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-full ${
              filledCount === 0
                ? "bg-rose-500/10 text-rose-600 dark:text-rose-400"
                : filledCount < 3
                ? "bg-amber-500/10 text-amber-600 dark:text-amber-400"
                : "bg-muted text-muted-foreground"
            }`}>
              {filledCount}/3 components
            </span>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2 text-xs">
            <ComponentCell icon={Package2} label="Bottle" item={bottle} />
            <ComponentCell icon={Factory} label="Cap" item={cap} />
            <ComponentCell icon={Box} label="Box" item={box} hint={`pack of ${variant.pack_size || 1}`} />
          </div>
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <Button variant="ghost" size="sm" onClick={onEdit} disabled={busy} data-testid={`size-bom-edit-${variant.id}`}>
            <Edit3 className="w-3.5 h-3.5 mr-1" /> Edit
          </Button>
          <Button variant="ghost" size="sm" onClick={onDuplicate} disabled={busy} data-testid={`size-bom-duplicate-${variant.id}`}>
            <Copy className="w-3.5 h-3.5 mr-1" /> Duplicate
          </Button>
          <Button variant="ghost" size="sm" onClick={onDelete} disabled={busy} className="text-rose-600 hover:text-rose-700 hover:bg-rose-500/10" data-testid={`size-bom-delete-${variant.id}`}>
            <Trash2 className="w-3.5 h-3.5" />
          </Button>
        </div>
      </div>
    </div>
  );
}

function ComponentCell({ icon: Icon, label, item, hint }) {
  return (
    <div className="flex items-start gap-2 rounded-md border border-border bg-background px-2 py-1.5">
      <Icon className="w-3.5 h-3.5 text-muted-foreground mt-0.5" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{label}</div>
        {item ? (
          <div>
            <div className="font-medium truncate" title={item.name}>{item.name}</div>
            <div className="text-[10px] text-muted-foreground font-mono truncate">{item.sku}{hint ? ` · ${hint}` : ""}</div>
          </div>
        ) : (
          <div className="text-muted-foreground italic">— Not set{hint ? ` · ${hint}` : ""}</div>
        )}
      </div>
    </div>
  );
}

function VariantFormDialog({ open, initial, size, items, defaultPack, existingVariants, onClose, onSaved }) {
  const isEdit = !!initial;
  const [name, setName] = useState("");
  const [bottle, setBottle] = useState("");
  const [cap, setCap] = useState("");
  const [box, setBox] = useState("");
  const [pack, setPack] = useState(defaultPack);
  const [makeDefault, setMakeDefault] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;
    if (initial) {
      setName(initial.name || "");
      setBottle(initial.bottle_item_id || "");
      setCap(initial.cap_item_id || "");
      setBox(initial.box_item_id || "");
      setPack(initial.pack_size || defaultPack);
      setMakeDefault(!!initial.is_default);
    } else {
      // Defaults for a brand-new variant
      const isFirst = (existingVariants || []).length === 0;
      setName(isFirst ? "Default" : "");
      setBottle("");
      setCap("");
      setBox("");
      setPack(defaultPack);
      setMakeDefault(isFirst);  // first variant for the size auto-becomes default
    }
  }, [open, initial, defaultPack, existingVariants]);

  const itemOptions = useMemo(
    () => items.map((i) => ({
      value: i.id,
      label: i.name,
      sublabel: `${i.sku} · ${i.quantity ?? 0} ${i.unit || ""}`,
    })),
    [items]
  );

  const submit = async (e) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error("Variant name is required (e.g. Standard, Premium, Eco)");
      return;
    }
    // Detect duplicate names within the same size (excluding self when editing)
    const dup = (existingVariants || []).find(
      (v) => (!isEdit || v.id !== initial?.id) && (v.name || "").toLowerCase() === trimmed.toLowerCase()
    );
    if (dup) {
      toast.error(`A variant named "${trimmed}" already exists for size ${size}`);
      return;
    }

    const packNum = Number(pack) || 1;
    if (packNum < 1) {
      toast.error("Pack size must be at least 1");
      return;
    }

    setSubmitting(true);
    try {
      if (isEdit) {
        await api.put(`/size-bom/by-id/${initial.id}`, {
          name: trimmed,
          bottle_item_id: bottle || null,
          cap_item_id: cap || null,
          box_item_id: box || null,
          pack_size: packNum,
          is_default: makeDefault ? true : undefined,
        });
        toast.success(`Saved "${trimmed}"`);
      } else {
        await api.post(`/size-bom`, {
          size,
          name: trimmed,
          bottle_item_id: bottle || null,
          cap_item_id: cap || null,
          box_item_id: box || null,
          pack_size: packNum,
          is_default: makeDefault,
        });
        toast.success(`Created "${trimmed}"`);
      }
      onSaved?.();
    } catch (err) {
      toast.error(extractErr(err, "Failed to save variant"));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="size-bom-variant-form">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? `Edit ${size} variant — ${initial?.name}` : `New ${size} variant`}
          </DialogTitle>
          <DialogDescription>
            Define which bottle, cap, and box this variant uses, and how many bottles fit in one box (pack size).
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Variant name *</Label>
            <Input
              autoFocus={!isEdit}
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="e.g. Standard, Premium, Eco, Customer-A"
              data-testid="size-bom-variant-name-input"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Package2 className="w-3 h-3" /> Bottle
            </Label>
            <Combobox
              options={itemOptions}
              value={bottle}
              onChange={setBottle}
              placeholder="Pick bottle SKU…"
              data-testid="size-bom-variant-bottle"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Factory className="w-3 h-3" /> Cap
            </Label>
            <Combobox
              options={itemOptions}
              value={cap}
              onChange={setCap}
              placeholder="Pick cap SKU…"
              data-testid="size-bom-variant-cap"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground flex items-center gap-1">
              <Box className="w-3 h-3" /> Box
            </Label>
            <Combobox
              options={itemOptions}
              value={box}
              onChange={setBox}
              placeholder="Pick box SKU…"
              data-testid="size-bom-variant-box"
            />
          </div>

          <div className="space-y-1.5">
            <Label className="text-xs uppercase tracking-wider text-muted-foreground">Bottles per box (pack size)</Label>
            <Input
              type="number"
              min="1"
              value={pack}
              onChange={(e) => setPack(e.target.value)}
              data-testid="size-bom-variant-pack"
            />
            <div className="text-[11px] text-muted-foreground">
              Each finished bottle deducts 1/{pack || 1} of a box. Common: 12 for 100/300ml, 6 for 1L, 1 for 5/10/20L.
            </div>
          </div>

          {!isEdit && (existingVariants || []).length > 0 && (
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-accent">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                data-testid="size-bom-variant-make-default"
              />
              <span>Make this the new default for size {size}</span>
            </label>
          )}
          {isEdit && !initial?.is_default && (
            <label className="flex items-center gap-2 text-sm cursor-pointer p-2 rounded-md hover:bg-accent">
              <input
                type="checkbox"
                checked={makeDefault}
                onChange={(e) => setMakeDefault(e.target.checked)}
                data-testid="size-bom-variant-make-default"
              />
              <span>Make this the new default for size {size}</span>
            </label>
          )}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={submitting} data-testid="size-bom-variant-submit">
              <Save className="w-4 h-4 mr-1.5" />
              {submitting ? "Saving…" : isEdit ? "Save changes" : "Create variant"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
